import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Control, Controller } from "react-hook-form";
import {
  FileUploaderDropContainer,
  FileUploaderItem,
  IconButton,
} from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import TextField from "../carbon/Form/TextField";
import DateField from "../carbon/Form/DateField";
import { CertificationsFormFields } from "./certificationsValidationSchema";
import { FileReader } from "..";
import { useFileInputUploader } from "../../hooks";

export interface CertificationRowProps {
  index: number;
  control: Control<CertificationsFormFields>;
  onRemove: () => void;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * One row of `CertificationsForm`'s `useFieldArray` — same "compose `carbon/Form/*` primitives
 * per row" pattern `AddressRow.tsx`/`BankingRow.tsx` use, plus the two things this task's brief
 * calls out as new: `expirationDate` via the new `DateField` primitive (`carbon/Form/DateField`,
 * first use in this migration), and a per-row file upload built the SAME way
 * `BasicInformationFileUpload.tsx` (Task 8) built its two upload slots —
 * `FileUploaderDropContainer`/`FileUploaderItem` wired to `useFileInputUploader`.
 *
 * `fileURL` is bound via a bare `Controller` (not `TextField`) since it's never a text input —
 * the upload UI IS the control, matching `AddressRow.tsx`'s own precedent of reaching for a bare
 * `Controller` when neither `TextField` nor `SelectField` fits.
 *
 * Real, disclosed behaviour change from the pre-Carbon form: each row now owns its OWN
 * `useFileInputUploader` instance, instead of one instance shared across a single "currently
 * being added" panel. The pre-Carbon form could only ever have one certification under
 * construction at a time (only one row was ever editable — the staging panel), so "one shared
 * uploader" and "one independent uploader per row" were behaviourally identical for it. Now that
 * `CertificationsForm.tsx` makes every row (existing and new) independently editable in place —
 * the same `useFieldArray` extension `AddressesForm.tsx` (Task 8) already made for addresses —
 * multiple rows CAN be mid-upload at once; giving each row its own uploader is the natural,
 * safe extension of that same pattern, not a new restriction or a lost guarantee.
 */
const CertificationRow = ({
  index,
  control,
  onRemove,
}: CertificationRowProps) => {
  const { t } = useTranslation();
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");
  const { mutate: uploadFile, isPending: isUploading } = useFileInputUploader();

  return (
    <div className="certifications-form__row">
      <div className="certifications-form__row-grid">
        <TextField
          name={`certifications.${index}.certificationName`}
          label={t("certifications.certificationName")}
          control={control}
        />
        <DateField
          name={`certifications.${index}.expirationDate`}
          label={t("certifications.expirationDate")}
          control={control}
        />
      </div>

      <Controller
        name={`certifications.${index}.fileURL`}
        control={control}
        render={({ field, fieldState }) => (
          <div className="certifications-form__row-file">
            <p className="certifications-form__row-file-label cds--label">
              {t("companyProfile.certificationFile")}
            </p>
            <FileUploaderDropContainer
              labelText={
                isUploading
                  ? t("cta.uploading")
                  : t("companyProfile.uploadCertification")
              }
              onAddFiles={(_event, { addedFiles }) => {
                const file = addedFiles[0];
                if (!file) return;

                if (file.size > MAX_FILE_SIZE_BYTES) {
                  setFileError(t("companyProfile.fileTooLarge"));
                  return;
                }

                setFileError("");
                setPendingFileName(file.name);
                uploadFile(file, {
                  onSuccess: (url: string) => field.onChange(url),
                });
              }}
            />
            {pendingFileName && (
              <FileUploaderItem
                name={pendingFileName}
                status={isUploading ? "uploading" : "complete"}
                onDelete={() => {
                  setPendingFileName(null);
                  field.onChange("");
                }}
              />
            )}
            {field.value && !pendingFileName && (
              <FileReader fileKey={field.value} text={t("cta.download")} />
            )}
            {(fieldState.error || fileError) && (
              <p className="certifications-form__row-file-error">
                {fieldState.error?.message || fileError}
              </p>
            )}
          </div>
        )}
      />

      <div className="certifications-form__row-actions">
        <IconButton
          kind="ghost"
          label={t("cta.remove")}
          align="top"
          onClick={onRemove}
        >
          <TrashCan />
        </IconButton>
      </div>
    </div>
  );
};

export default CertificationRow;
